import React, { useEffect, useMemo, useRef, useState } from 'react';
import { ChevronDown, ChevronRight, Search, Server, Tag } from 'lucide-react';
import {
  ReleaseNoteVersion,
  DeployedVersion,
  RELEASE_NOTE_CATEGORIES,
  CATEGORY_BADGE_CLASSES,
  MODULE_LABELS,
  MODULE_ORDER,
  parseModules
} from '../../types/ReleaseNote';
import { releaseNotesApi } from '../../services/releaseNotesApi';

const formatDate = (value?: string): string => {
  if (!value) return '';
  const d = new Date(value);
  if (isNaN(d.getTime())) return '';
  return d.toLocaleDateString('nb-NO', { year: 'numeric', month: 'long', day: 'numeric' });
};

const formatShortDate = (value?: string): string => {
  if (!value) return '';
  const d = new Date(value);
  if (isNaN(d.getTime())) return '';
  return d.toLocaleDateString('nb-NO', { year: 'numeric', month: '2-digit', day: '2-digit' });
};

const CategoryBadge: React.FC<{ category: string }> = ({ category }) => {
  const classes = CATEGORY_BADGE_CLASSES[category] || 'bg-gray-100 text-gray-800 border-gray-200';
  return (
    <span className={`inline-flex items-center rounded-full border px-2.5 py-0.5 text-xs font-medium ${classes}`}>
      {category}
    </span>
  );
};

const ModuleBadges: React.FC<{ modules?: string }> = ({ modules }) => {
  const codes = parseModules(modules);
  if (codes.length === 0) return null;
  return (
    <div className="flex flex-wrap gap-1 mt-1">
      {codes.map(code => (
        <span
          key={code}
          className="inline-flex items-center rounded border border-indigo-200 bg-indigo-100 px-1.5 py-0.5 text-[11px] font-medium text-indigo-700"
        >
          {MODULE_LABELS[code] || code}
        </span>
      ))}
    </div>
  );
};

const ModuleFilter: React.FC<{ selected: string[]; onChange: (next: string[]) => void }> = ({ selected, onChange }) => {
  const [open, setOpen] = useState(false);
  const ref = useRef<HTMLDivElement>(null);

  useEffect(() => {
    const onDoc = (e: MouseEvent) => {
      if (ref.current && !ref.current.contains(e.target as Node)) setOpen(false);
    };
    document.addEventListener('mousedown', onDoc);
    return () => document.removeEventListener('mousedown', onDoc);
  }, []);

  const toggle = (code: string) =>
    onChange(selected.includes(code) ? selected.filter(c => c !== code) : [...selected, code]);

  const label = selected.length === 0 ? 'Alle moduler' : `${selected.length} modul${selected.length === 1 ? '' : 'er'} valgt`;

  return (
    <div className="relative" ref={ref}>
      <button
        type="button"
        onClick={() => setOpen(o => !o)}
        aria-label="Filtrer på modul"
        className="flex items-center gap-2 border border-gray-300 rounded-md text-sm px-3 py-2 bg-white hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-blue-500"
      >
        {label}
        <ChevronDown className="h-4 w-4 text-gray-400" />
      </button>
      {open && (
        <div className="absolute right-0 z-10 mt-1 w-56 max-h-72 overflow-y-auto rounded-md border border-gray-200 bg-white shadow-lg p-1">
          {selected.length > 0 && (
            <button
              type="button"
              onClick={() => onChange([])}
              className="w-full text-left px-2 py-1.5 text-xs text-blue-600 hover:bg-gray-50 rounded"
            >
              Nullstill
            </button>
          )}
          {MODULE_ORDER.map(code => (
            <label
              key={code}
              className="flex items-center gap-2 px-2 py-1.5 text-sm text-gray-700 hover:bg-gray-50 rounded cursor-pointer"
            >
              <input
                type="checkbox"
                checked={selected.includes(code)}
                onChange={() => toggle(code)}
                className="rounded border-gray-300 text-blue-600 focus:ring-blue-500"
              />
              {MODULE_LABELS[code]}
            </label>
          ))}
        </div>
      )}
    </div>
  );
};

const ReleaseNotes: React.FC = () => {
  const [versions, setVersions] = useState<ReleaseNoteVersion[]>([]);
  const [deployed, setDeployed] = useState<DeployedVersion[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  const [searchTerm, setSearchTerm] = useState('');
  const [selectedCategory, setSelectedCategory] = useState<string>('all');
  const [selectedModules, setSelectedModules] = useState<string[]>([]);
  const [expanded, setExpanded] = useState<Set<string>>(new Set());

  // Deploy-historikk (lazy-lastet, skjult initielt)
  const [showHistory, setShowHistory] = useState(false);
  const [history, setHistory] = useState<DeployedVersion[] | null>(null);
  const [historyLoading, setHistoryLoading] = useState(false);
  const [historyError, setHistoryError] = useState<string | null>(null);

  useEffect(() => {
    const load = async () => {
      try {
        setLoading(true);
        setError(null);
        const [groups, deployedVersions] = await Promise.all([
          releaseNotesApi.getGrouped(),
          releaseNotesApi.getDeployedVersions().catch(() => [] as DeployedVersion[])
        ]);
        setVersions(groups);
        setDeployed(deployedVersions);
        // Expand the newest version by default
        if (groups.length > 0) {
          setExpanded(new Set([groups[0].version]));
        }
      } catch (err) {
        console.error('Error fetching release notes:', err);
        setError('Feil ved henting av release notes');
      } finally {
        setLoading(false);
      }
    };
    load();
  }, []);

  const toggleHistory = async () => {
    const next = !showHistory;
    setShowHistory(next);
    if (next && history === null && !historyLoading) {
      try {
        setHistoryLoading(true);
        setHistoryError(null);
        const data = await releaseNotesApi.getDeployedVersionHistory();
        setHistory(data);
      } catch (err) {
        console.error('Error fetching deploy history:', err);
        setHistoryError('Feil ved henting av deploy-historikk');
      } finally {
        setHistoryLoading(false);
      }
    }
  };

  const toggleVersion = (version: string) => {
    setExpanded(prev => {
      const next = new Set(prev);
      if (next.has(version)) {
        next.delete(version);
      } else {
        next.add(version);
      }
      return next;
    });
  };

  // Filter notes within each version; drop versions that end up empty.
  const filteredVersions = useMemo(() => {
    const term = searchTerm.trim().toLowerCase();
    return versions
      .map(v => ({
        ...v,
        notes: v.notes.filter(n => {
          const matchesCategory = selectedCategory === 'all' || n.category === selectedCategory;
          const noteModules = parseModules(n.modules);
          const matchesModule = selectedModules.length === 0 || noteModules.some(m => selectedModules.includes(m));
          const matchesSearch =
            term === '' ||
            n.title.toLowerCase().includes(term) ||
            (n.description?.toLowerCase().includes(term) ?? false) ||
            (n.impact?.toLowerCase().includes(term) ?? false);
          return matchesCategory && matchesModule && matchesSearch;
        })
      }))
      .filter(v => v.notes.length > 0);
  }, [versions, searchTerm, selectedCategory, selectedModules]);

  const isFiltering = searchTerm.trim() !== '' || selectedCategory !== 'all' || selectedModules.length > 0;

  if (loading) {
    return (
      <div className="flex items-center justify-center py-16">
        <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-blue-600"></div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="rounded-md bg-red-50 border border-red-200 p-4 text-sm text-red-700">
        {error}
      </div>
    );
  }

  return (
    <div className="space-y-6">
      {/* Hva kjører nå-banner */}
      {deployed.length > 0 && (
        <div className="rounded-lg border border-blue-200 bg-blue-50 px-4 py-3">
          <div className="flex items-center text-sm font-medium text-blue-900 mb-2">
            <Server className="h-4 w-4 mr-2" />
            Hva kjører nå
          </div>
          <div className="flex flex-wrap gap-2">
            {deployed.map(d => (
              <span
                key={d.environment}
                title={`${d.version}${d.gitSha ? ` · ${d.gitSha}` : ''}\nUtrullet: ${formatDate(d.deployedAt)}\nSist sett: ${formatDate(d.lastSeenAt)}`}
                className="inline-flex items-center rounded-md bg-white border border-blue-200 px-2.5 py-1 text-xs text-gray-700"
              >
                <span className="font-semibold text-gray-900 mr-1">{d.environment}:</span>
                {d.version}
                {d.deployedAt && (
                  <span className="text-gray-400 ml-1.5">· {formatShortDate(d.deployedAt)}</span>
                )}
              </span>
            ))}
          </div>

          <button
            onClick={toggleHistory}
            className="mt-3 inline-flex items-center text-xs font-medium text-blue-700 hover:text-blue-900"
          >
            {showHistory ? (
              <ChevronDown className="h-3.5 w-3.5 mr-1" />
            ) : (
              <ChevronRight className="h-3.5 w-3.5 mr-1" />
            )}
            {showHistory ? 'Skjul deploy-historikk' : 'Vis deploy-historikk'}
          </button>

          {showHistory && (
            <div className="mt-3 border-t border-blue-200 pt-3">
              {historyLoading ? (
                <div className="text-xs text-gray-500">Laster historikk…</div>
              ) : historyError ? (
                <div className="text-xs text-red-700">{historyError}</div>
              ) : history && history.length > 0 ? (
                <div className="overflow-x-auto rounded-md bg-white border border-blue-200">
                  <table className="min-w-full text-xs">
                    <thead>
                      <tr className="text-left text-gray-500 border-b border-gray-100">
                        <th className="py-2 px-3 font-medium">Miljø</th>
                        <th className="py-2 px-3 font-medium">Versjon</th>
                        <th className="py-2 px-3 font-medium">Utrullet</th>
                        <th className="py-2 px-3 font-medium">Sist sett</th>
                        <th className="py-2 px-3 font-medium">Maskin</th>
                        <th className="py-2 px-3 font-medium">GitSha</th>
                      </tr>
                    </thead>
                    <tbody className="text-gray-700">
                      {history.map(h => (
                        <tr key={h.id} className="border-b border-gray-50 last:border-0">
                          <td className="py-2 px-3 font-medium text-gray-900">{h.environment}</td>
                          <td className="py-2 px-3">{h.version}</td>
                          <td className="py-2 px-3 whitespace-nowrap">{formatDate(h.deployedAt)}</td>
                          <td className="py-2 px-3 whitespace-nowrap">{formatDate(h.lastSeenAt)}</td>
                          <td className="py-2 px-3">{h.machineName || '—'}</td>
                          <td className="py-2 px-3 font-mono">{h.gitSha ? h.gitSha.substring(0, 8) : '—'}</td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              ) : (
                <div className="text-xs text-gray-500">Ingen deploy-historikk.</div>
              )}
            </div>
          )}
        </div>
      )}

      {/* Filter + søk */}
      <div className="flex flex-col sm:flex-row sm:items-center gap-3">
        <div className="flex flex-wrap gap-2">
          <button
            onClick={() => setSelectedCategory('all')}
            className={`px-3 py-1 rounded-full text-xs font-medium border ${
              selectedCategory === 'all'
                ? 'bg-gray-800 text-white border-gray-800'
                : 'bg-white text-gray-600 border-gray-300 hover:bg-gray-50'
            }`}
          >
            Alle
          </button>
          {RELEASE_NOTE_CATEGORIES.map(cat => (
            <button
              key={cat}
              onClick={() => setSelectedCategory(cat)}
              className={`px-3 py-1 rounded-full text-xs font-medium border ${
                selectedCategory === cat
                  ? 'bg-gray-800 text-white border-gray-800'
                  : 'bg-white text-gray-600 border-gray-300 hover:bg-gray-50'
              }`}
            >
              {cat}
            </button>
          ))}
        </div>
        <div className="flex items-center gap-2 sm:ml-auto">
          <ModuleFilter selected={selectedModules} onChange={setSelectedModules} />
          <div className="relative sm:w-64">
            <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-gray-400" />
            <input
              type="text"
              value={searchTerm}
              onChange={e => setSearchTerm(e.target.value)}
              placeholder="Søk i release notes..."
              className="w-full pl-9 pr-3 py-2 border border-gray-300 rounded-md text-sm focus:outline-none focus:ring-2 focus:ring-blue-500"
            />
          </div>
        </div>
      </div>

      {/* Tidslinje per versjon */}
      {filteredVersions.length === 0 ? (
        <div className="text-center py-12 text-gray-500 text-sm">
          {isFiltering ? 'Ingen release notes matcher filteret.' : 'Ingen release notes er registrert ennå.'}
        </div>
      ) : (
        <div className="space-y-3">
          {filteredVersions.map(version => {
            const isOpen = isFiltering || expanded.has(version.version);
            return (
              <div key={version.version} className="border border-gray-200 rounded-lg overflow-hidden">
                <button
                  onClick={() => toggleVersion(version.version)}
                  className="w-full flex items-center justify-between px-4 py-3 bg-gray-50 hover:bg-gray-100 text-left"
                >
                  <div className="flex items-center gap-3">
                    {isOpen ? (
                      <ChevronDown className="h-4 w-4 text-gray-400" />
                    ) : (
                      <ChevronRight className="h-4 w-4 text-gray-400" />
                    )}
                    <span className="inline-flex items-center rounded-md bg-blue-600 text-white px-2.5 py-1 text-sm font-semibold">
                      <Tag className="h-3.5 w-3.5 mr-1" />
                      v{version.version}
                    </span>
                    <span className="text-sm text-gray-500">{formatDate(version.releaseDate)}</span>
                  </div>
                  <span className="text-xs text-gray-400">
                    {version.notes.length} {version.notes.length === 1 ? 'endring' : 'endringer'}
                  </span>
                </button>

                {isOpen && (
                  <ul className="divide-y divide-gray-100">
                    {version.notes.map(note => (
                      <li key={note.id} className="px-4 py-3">
                        <div className="flex items-start gap-3">
                          <div className="pt-0.5">
                            <CategoryBadge category={note.category} />
                          </div>
                          <div className="min-w-0">
                            <p className="font-semibold text-gray-900">{note.title}</p>
                            <ModuleBadges modules={note.modules} />
                            {note.description && (
                              <p className="text-sm text-gray-700 mt-0.5 whitespace-pre-line">{note.description}</p>
                            )}
                            {note.impact && (
                              <p className="text-xs text-gray-500 mt-1 whitespace-pre-line">{note.impact}</p>
                            )}
                          </div>
                        </div>
                      </li>
                    ))}
                  </ul>
                )}
              </div>
            );
          })}
        </div>
      )}
    </div>
  );
};

export default ReleaseNotes;
